import { useRef, useEffect, useState } from "react";

interface VideoThumbnailProps {
  src: string;
  title: string;
  className?: string;
}

/**
 * VideoThumbnail — lazy-loads video thumbnails via IntersectionObserver.
 *
 * CORS strategy:
 * 1. Fetch the video URL as a Blob (public Supabase Storage URLs allow CORS fetch)
 * 2. Create a same-origin blob: URL from the Blob
 * 3. Load the blob URL in a video element — Canvas sees it as same-origin
 * 4. Capture first frame → revoke blob URL
 *
 * This bypasses the Canvas tainting issue entirely, because blob: URLs are
 * same-origin regardless of where the original video came from.
 */
export default function VideoThumbnail({ src, title, className = "" }: VideoThumbnailProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [poster, setPoster] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [isInView, setIsInView] = useState(false);

  const capturedRef = useRef(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const blobUrlRef = useRef<string | null>(null);

  // ── IntersectionObserver lazy loading ──────────────────────────
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // ── Blob-based video frame capture ────────────────────────────
  useEffect(() => {
    if (!isInView || capturedRef.current) return;

    let cancelled = false;

    const captureFrame = (video: HTMLVideoElement): boolean => {
      if (cancelled || capturedRef.current) return false;
      const vw = video.videoWidth || 320;
      const vh = video.videoHeight || 180;
      if (vw <= 0 || vh <= 0) return false;

      const canvas = document.createElement("canvas");
      canvas.width = vw;
      canvas.height = vh;
      const ctx = canvas.getContext("2d");
      if (!ctx) return false;

      try {
        ctx.drawImage(video, 0, 0, vw, vh);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.8);
        if (!cancelled) {
          setPoster(dataUrl);
          capturedRef.current = true;
        }
        return true;
      } catch {
        if (!cancelled) setError(true);
        return false;
      }
    };

    const loadAndCapture = async () => {
      try {
        // Step 1: Fetch video as Blob
        const response = await fetch(src);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const blob = await response.blob();
        if (cancelled) return;

        // Step 2: Create same-origin blob URL (no more CORS tainting!)
        const blobUrl = URL.createObjectURL(blob);
        blobUrlRef.current = blobUrl;

        // Step 3: Create off-screen video element
        const video = document.createElement("video");
        video.src = blobUrl;
        video.preload = "auto";
        video.playsInline = true;
        video.muted = true;
        video.setAttribute("playsinline", "");
        video.style.cssText =
          "position:fixed;top:-9999px;left:-9999px;width:1px;height:1px;opacity:0;pointer-events:none;";
        document.body.appendChild(video);
        videoRef.current = video;

        let fallbackTimer: ReturnType<typeof setTimeout> | null = null;

        const trySeekAndCapture = () => {
          if (cancelled || capturedRef.current) return;
          video.currentTime = 0.1;

          fallbackTimer = setTimeout(() => {
            if (cancelled || capturedRef.current) return;
            video.play().then(() => {
              video.pause();
              captureFrame(video);
            }).catch(() => {
              captureFrame(video);
            });
          }, 1000);
        };

        const onLoadedMetadata = () => {
          if (cancelled || capturedRef.current) return;
          trySeekAndCapture();
        };

        const onSeeked = () => {
          if (cancelled || capturedRef.current) return;
          if (fallbackTimer) clearTimeout(fallbackTimer);
          setTimeout(() => captureFrame(video), 100);
        };

        const onError = () => {
          if (!cancelled) setError(true);
        };

        video.addEventListener("loadedmetadata", onLoadedMetadata);
        video.addEventListener("seeked", onSeeked);
        video.addEventListener("error", onError);

        if (video.readyState >= 1) {
          trySeekAndCapture();
        }

        video.load();
      } catch {
        if (!cancelled) setError(true);
      }
    };

    loadAndCapture();

    return () => {
      cancelled = true;
      // Cleanup video element
      const video = videoRef.current;
      if (video) {
        if (video.parentNode) video.parentNode.removeChild(video);
        videoRef.current = null;
      }
      // Revoke blob URL
      const blobUrl = blobUrlRef.current;
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
        blobUrlRef.current = null;
      }
    };
  }, [isInView, src]);

  // ── Render states ──────────────────────────────────────────────

  if (!isInView) {
    return <div ref={containerRef} className={`bg-gray-100 ${className}`} />;
  }

  if (error) {
    return (
      <div
        ref={containerRef}
        className={`bg-gray-200 flex items-center justify-center ${className}`}
      >
        <i className="ri-video-line text-gray-400 text-2xl"></i>
      </div>
    );
  }

  if (poster) {
    return (
      <div ref={containerRef} className={className}>
        <img src={poster} alt={title} className="w-full h-full object-cover" />
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`bg-gray-200 flex items-center justify-center ${className}`}
    >
      <i className="ri-loader-4-line animate-spin text-gray-400 text-xl"></i>
    </div>
  );
}